import {
  AIR_DAMPING,
  BALL_RADIUS,
  GRAVITY_SOFTENING,
  MAX_ACCEL,
  PHYSICS_DT,
  PLANET_RESTITUTION,
  WALL_RESTITUTION,
  type ArenaCapsule,
  type BallState,
  type HoleLayout,
  type Planet,
} from "@shared/physics";
import { mulberry32, randRange, seedFromString } from "@shared/rng";
import type { SceneCtx } from "../scene";
import { biomeForHole } from "@shared/biome";
import { setActiveTheme } from "../theme";
import { buildHoleVisuals, updateTrail, type HoleVisuals } from "./render";
import { HoleCamera } from "./camera";
import { installAim } from "./aim";
import { sfx, startCharge, startRunnerMusic, stopRunnerMusic } from "../audio";
import {
  AdditiveBlending,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector3,
} from "three";
import { createSparks } from "./sparks";

export interface RunnerHandle {
  dispose(): void;
}

export interface RunnerOptions {
  rngSeed: string;
  /** Wired to HUD chrome. Score + combo replace the old multiplier;
   *  combo grows with consecutive orb pickups, decays after a miss. */
  onStateChange?: (state: {
    distance: number;
    speed: number;
    lives: number;
    score: number;
    combo: number;
    bestScore: number;
    /** True until the player has fired their launch shot — HUD shows
     *  the "drag to launch" hint while false. */
    started: boolean;
  }) => void;
  /** Fired when the ball runs out of lives. */
  onGameOver?: (final: {
    distance: number;
    score: number;
    bestScore: number;
  }) => void;
  /** Fired when the player taps a "back to menu" CTA on the game-over
   *  card so main.ts can swap UI back. */
  onExitToMenu?: () => void;
}

const BALL_Y = BALL_RADIUS + 0.06;
// Capsule "tube" radius — wide enough that the player has lateral
// breathing room but narrow enough that planets feel like real
// obstacles you have to dodge around.
const TUBE_RADIUS = 9;
// Spacing between mini-capsule waypoints along the wave track.
// Smaller = the wave reads as a smooth curve instead of a faceted
// polyline. We bumped MAX_CAPSULES_IN_SHADER from 24 → 64 in
// render.ts specifically so we can keep this small (9u per
// capsule) while still seeing ~540u ahead.
const TRACK_STEP = 9;
// Pre-build this many world-units of corridor ahead of the ball so
// the far horizon is always populated. Generous enough that a single
// rebuild covers ~25-30s of forward motion at cruising speed.
const LOOKAHEAD = 800;
// Rebuild visuals when the ball has moved this far since the last
// rebuild. Tuned to be well below VISIBLE_FORWARD (=540) so we
// always have ~180u of corridor still rendered ahead of the ball
// when the rebuild fires — the player never sees the corridor
// visibly end. Each rebuild re-creates planet meshes + shader
// uniforms + particles (30-50ms hit), so we throttle aggressively
// to keep frame rate steady.
const VISUAL_REBUILD_THRESHOLD = 360;
// Forward thrust applied every physics step (only after the player
// has fired the launch shot). Scales gently with distance so the run
// builds without snapping.
const BASE_FWD_ACCEL = 11;
const MAX_FWD_ACCEL_BONUS = 12;
// Cap on the ball's overall speed — keeps the run controllable even
// at deep distances.
const MAX_SPEED = 38;
// Critically-damped spring constants for centerline alignment.
// Tuned so the ball rails along the wave smoothly without bouncing
// back and forth across the centerline. Lower stiffness + matched
// damping = no oscillation.
const CENTERLINE_STIFFNESS = 9;
const CENTERLINE_DAMPING = 6;
// Ms of invulnerability after a planet hit so we don't drain all 3
// lives in one collision frame.
const HIT_IFRAMES_MS = 1100;
const STARTING_LIVES = 3;

// Pickup tuning — orbs sprinkled along the track centerline. Each
// pickup is worth 100 base points × the current combo multiplier.
// The combo grows on each consecutive collect and decays on a miss.
const PICKUP_SPACING = 22;
const PICKUP_RADIUS = 0.8;
const PICKUP_BASE_VALUE = 100;
const COMBO_MAX = 8;
// Combo "miss" timeout — a few seconds without a pickup resets it.
// Without this, a player who slows down loses combo silently.
const COMBO_TIMEOUT_MS = 4500;

// localStorage key for the per-device best score. Persists across
// reloads but we don't sync it server-side — runner is a fun
// side-mode, not a competitive ladder.
const BEST_SCORE_KEY = "cosmic-par/runner-best-score";

function loadBestScore(): number {
  try {
    const v = Number(localStorage.getItem(BEST_SCORE_KEY) ?? "0");
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}
function saveBestScore(s: number): void {
  try {
    localStorage.setItem(BEST_SCORE_KEY, String(Math.round(s)));
  } catch {
    /* private mode etc — silently ignore */
  }
}

// ---------- Wave track ----------
//
// Deterministic, smooth sine track. Ball position x maps to a
// centerline z via two harmonics. Difficulty scales the amplitude
// over distance so the curves get bendier the further you go without
// ever jarring.
function trackZ(x: number): number {
  const difficulty = Math.min(1.6, x / 800);
  const a1 = 4.5 + difficulty * 2.5;
  const a2 = 1.8 + difficulty * 1.2;
  return Math.sin(x * 0.024) * a1 + Math.sin(x * 0.071 + 1.3) * a2;
}
// Numeric derivative of `trackZ` — used both to tilt the corridor's
// capsules so they hug the curve, and to project ball velocity onto
// the local "forward" direction.
function trackDz(x: number): number {
  const h = 0.5;
  return (trackZ(x + h) - trackZ(x - h)) / (2 * h);
}

// Build one mini-capsule that hugs the wave between x=startX and
// x=startX+TRACK_STEP. Tilts the capsule along the local trackline
// tangent so consecutive capsules form a smooth tube the eye reads
// as one continuous bending corridor.
function buildTrackCapsule(startX: number): ArenaCapsule {
  return {
    ax: startX,
    az: trackZ(startX),
    bx: startX + TRACK_STEP,
    bz: trackZ(startX + TRACK_STEP),
    radius: TUBE_RADIUS,
  };
}

// Sample a few planet candidates within `[startX, startX+TRACK_STEP]`.
// Places at most one planet per step, biased off the track centerline
// so the player has to actively weave around it. Empty for the first
// stretch so the run opens with calm setup.
function maybeSpawnPlanet(
  rng: () => number,
  segStartX: number,
  difficulty: number,
): Planet | null {
  if (segStartX < 90) return null; // grace zone — no obstacles in first ~90m
  // Density ramps with difficulty: ~25% chance at d=1, up to ~70% at d=4+.
  const chance = Math.min(0.7, 0.18 + difficulty * 0.13);
  if (rng() > chance) return null;
  const cx = segStartX + TRACK_STEP * 0.5;
  // Offset perpendicular to the local tangent so the planet sits
  // OFF the trackline — collectibles ride the trackline, planets
  // straddle it.
  const dz = trackDz(cx);
  const tlen = Math.hypot(1, dz);
  const px = -dz / tlen;
  const pz = 1 / tlen;
  const offset =
    (rng() < 0.5 ? -1 : 1) *
    randRange(rng, 1.6, Math.min(5, 2.4 + difficulty * 0.6));
  return {
    cx: cx + px * offset,
    cz: trackZ(cx) + pz * offset,
    radius: randRange(rng, 1.0, 1.6),
    strength: 38 + difficulty * 6,
    // Stable visual seed — survives rebuild-window churn so the
    // planet's colour/type/spin/tilt/rings stay locked even as the
    // sliding window shifts which planets are in view. Without this,
    // each rebuild re-shuffled every visible planet's appearance.
    seed: (rng() * 0xffffffff) >>> 0,
  };
}

// Distance from point P to the closest point on the capsule's axis
// segment AB. Used both for collision and for proximity-multiplier
// gain.
function distPointSegment(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const len2 = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2));
  const cx = ax + dx * t;
  const cz = az + dz * t;
  return Math.hypot(px - cx, pz - cz);
}

export function startRunner(
  scene: SceneCtx,
  opts: RunnerOptions,
): RunnerHandle {
  let alive = true;

  // Procedural state — keyed off the supplied seed so reload-restart
  // doesn't replay the exact same RNG sequence. Track shape is
  // deterministic (sine harmonics on x), so every run sees the same
  // wave; only planet/pickup placement varies with the seed.
  const rng = mulberry32(seedFromString(opts.rngSeed));
  const segments: ArenaCapsule[] = [];
  let allPlanets: Planet[] = [];
  // Pickups: one collectible orb per chunk along the centerline.
  // `mesh` is null for already-collected (cleanup happens lazily).
  type Pickup = {
    x: number;
    z: number;
    collected: boolean;
    mesh: Mesh | null;
  };
  const pickups: Pickup[] = [];
  let lastBuiltX = 0;
  let lastPickupX = 0;
  // Tracks the ball.x at the last rebuildVisuals() call so we can
  // throttle rebuilds. Initial -Infinity forces the first build.
  let lastRebuildBallX = -Infinity;
  // Hold all pickup meshes in a single Group so we can add/remove in
  // bulk without polluting the HoleVisuals tree.
  const pickupRoot = new Group();
  scene.scene.add(pickupRoot);
  const pickupGeom = new SphereGeometry(PICKUP_RADIUS, 12, 10);
  const pickupColor = new Color("#ffd86a");

  function spawnPickup(x: number): void {
    const mat = new MeshBasicMaterial({
      color: pickupColor,
      transparent: true,
      opacity: 0.95,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new Mesh(pickupGeom, mat);
    const z = trackZ(x);
    mesh.position.set(x, BALL_Y, z);
    pickupRoot.add(mesh);
    pickups.push({ x, z, collected: false, mesh });
  }

  function ensureCorridor(targetX: number): void {
    // PHYSICS state extension is cheap (just numbers in arrays) — keep
    // it on the hot path. Visual rebuild is heavy (meshes, shaders,
    // particles) so we throttle that separately below.
    while (lastBuiltX < targetX + LOOKAHEAD) {
      const startX = lastBuiltX;
      const difficulty = startX / 220;
      segments.push(buildTrackCapsule(startX));
      const p = maybeSpawnPlanet(rng, startX, difficulty);
      if (p) allPlanets.push(p);
      lastBuiltX += TRACK_STEP;
    }
    // Pickups every PICKUP_SPACING along the centerline. Independent
    // cadence from segment generation. Pickup meshes are added
    // incrementally to a separate `pickupRoot` Group, so spawning is
    // cheap and DOESN'T need a rebuildVisuals — they appear
    // immediately on the next render frame.
    while (lastPickupX < targetX + LOOKAHEAD - PICKUP_SPACING) {
      lastPickupX += PICKUP_SPACING;
      // Skip the very first pickup (x < 30) so the launch shot has
      // empty space to find its rhythm.
      if (lastPickupX > 30) spawnPickup(lastPickupX);
    }
    // Prune behind-ball state — keeps the segments + planets array
    // bounded on long runs without disturbing visuals.
    const cutoff = ball.x - 80;
    while (segments.length > 0 && segments[0]!.bx < cutoff) {
      segments.shift();
    }
    allPlanets = allPlanets.filter((p) => p.cx > cutoff);
    // Drop pickups that are far behind regardless of collected state.
    for (let i = pickups.length - 1; i >= 0; i--) {
      const p = pickups[i]!;
      if (p.x < cutoff) {
        if (p.mesh) {
          pickupRoot.remove(p.mesh);
          (p.mesh.material as MeshBasicMaterial).dispose();
        }
        pickups.splice(i, 1);
      }
    }
    // Visual rebuild: only when the ball has moved a meaningful
    // distance since the last rebuild. New corridor / planets /
    // boundary will pop in once per ~12s instead of once per ~1s.
    if (ball.x - lastRebuildBallX > VISUAL_REBUILD_THRESHOLD) {
      rebuildVisuals();
      lastRebuildBallX = ball.x;
    }
  }

  // Build a synthetic HoleLayout from the active corridor so we can
  // reuse `buildHoleVisuals` — gives us all the shader-rendered
  // capsule arena, planet meshes, gravity wells, etc. for free.
  // The render-side capsule shader caps at 24 entries, so we send
  // a sliding window of segments AROUND the ball rather than the
  // entire history. Planets are filtered the same way so the
  // gravity wells in view match the corridor in view.
  //
  // VISIBLE_FORWARD must keep us well past the rebuild threshold so
  // the player NEVER sees the corridor visibly end ahead of them.
  // At TRACK_STEP=9, 540u → ~60 capsules, comfortably under the
  // 64-cap shader limit (MAX_CAPSULES_IN_SHADER in render.ts).
  function buildLayout(): HoleLayout {
    const VISIBLE_BACK = 90;
    const VISIBLE_FORWARD = 540;
    const visibleSegments = segments.filter(
      (s) => s.bx > ball.x - VISIBLE_BACK && s.ax < ball.x + VISIBLE_FORWARD,
    );
    const visiblePlanets = allPlanets.filter(
      (p) => p.cx > ball.x - VISIBLE_BACK && p.cx < ball.x + VISIBLE_FORWARD,
    );
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    for (const s of visibleSegments) {
      minX = Math.min(minX, s.ax - s.radius, s.bx - s.radius);
      maxX = Math.max(maxX, s.ax + s.radius, s.bx + s.radius);
      minZ = Math.min(minZ, s.az - s.radius, s.bz - s.radius);
      maxZ = Math.max(maxZ, s.az + s.radius, s.bz + s.radius);
    }
    return {
      index: 0,
      seed: 0,
      bounds: { minX, maxX, minZ, maxZ },
      arenaSegments: visibleSegments,
      // Tee parked WAY behind the ball so its visual ring (always
      // rendered by `buildHoleVisuals` at the layout's tee point)
      // stays offscreen. Runner mode has no tees; the ring would
      // otherwise spawn at (0,0) and look like a misplaced marker
      // next to where the ball actually starts.
      tee: { x: -2000, z: 0 },
      // Target parked WAY ahead of the ball so its black-hole visual
      // is always offscreen (buildHoleVisuals always renders one). The
      // camera reads its (cx, cz) for follow-lookahead so the player
      // sees what's coming, but the target itself never shows up.
      target: { cx: ball.x + 5000, cz: ball.z, radius: 0.001 },
      planets: visiblePlanets,
      par: 0,
    };
  }

  let visuals: HoleVisuals | null = null;
  let camera: HoleCamera | null = null;
  // Reused per-frame vectors for the runner-specific camera override
  // so the onFrame hook doesn't allocate every tick.
  const runnerCamPos = new Vector3();
  const runnerCamLook = new Vector3();

  function disposeVisuals(): void {
    if (visuals) {
      scene.scene.remove(visuals.root);
      visuals.dispose();
      visuals = null;
    }
  }

  function rebuildVisuals(): void {
    disposeVisuals();
    const layout = buildLayout();
    visuals = buildHoleVisuals(layout);
    scene.scene.add(visuals.root);
    // Camera setup happens ONCE — on the very first build. Calling
    // `setHole` on every corridor extension was the source of the
    // jaggy up/down jitter: each call ran `frameHole()` which reset
    // the desired camera position based on the layout's bounds, and
    // since we extend the corridor many times per second, the camera
    // never settled. The first call is enough; followBall takes over
    // for live tracking after that.
    if (!camera) {
      camera = new HoleCamera(scene.camera, layout);
      camera.setHole(layout, { intro: false });
    }
    if (visuals) {
      visuals.ball.position.set(ball.x, BALL_Y, ball.z);
    }
  }

  // ----- runtime state -----
  // Ball auto-flows forward from the start — no launch shot. Drag-
  // and-release is still wired but acts as a mid-flight lateral
  // nudge (helps players steer between planets).
  const ball: BallState = { x: 0, z: trackZ(0), vx: 12, vz: 0 };
  let lives = STARTING_LIVES;
  let bestScore = loadBestScore();
  let score = 0;
  let combo = 1;
  let lastPickupAt = -Infinity;
  let lastHitAt = -Infinity;
  // Held-state lateral steering. Updated continuously by pointer drag
  // (onAimMove) or by a held arrow / WASD key. step() drains it as a
  // continuous lateral acceleration so the ball reacts the moment the
  // player moves their finger, not on release.
  let steering: { dx: number; dz: number; mag: number } | null = null;
  let dead = false;
  // Always "started" — runner mode now flows automatically. The flag
  // is kept on the state object only so the HUD's "started: true"
  // signal continues to hide the launch hint without extra plumbing.
  const started = true;
  // Trail history — ring buffer of the last ~240 ball positions so
  // updateTrail has something to paint. We don't use simulateShot
  // here so the simulator doesn't supply samples; we track our own.
  const trailHistory: { x: number; y: number; z: number }[] = [];
  const TRAIL_LIMIT = 240; // ~4s of trail at 60Hz

  // Reuse the same sparks pool the live game uses for planet hits +
  // wormhole pops. Adds collision punch without bespoke effects.
  const sparks = createSparks();
  scene.scene.add(sparks.points);
  // Per-planet contact cooldown so a single sustained collision
  // (ball grinding along a planet's surface) emits one burst, not
  // sixty.
  const planetSparkCooldown = new Map<Planet, number>();
  const SPARK_COOLDOWN_MS = 250;

  // Single stable theme for the entire run — picked once from the
  // session's RNG seed and never changed. Rotating it per "sector"
  // caused planet hues to flip on every rebuild because
  // buildPlanetShader reads `theme.planetHueStart/Span` at build
  // time. Keeping the theme constant + per-planet stable seeds = no
  // visible colour flips when the corridor extends.
  setActiveTheme(biomeForHole(opts.rngSeed, 0));
  scene.backdrop.applyTheme();
  ensureCorridor(0);

  // Procedural high-energy chase music. Routed through the shared
  // audio engine so the global mute / volume controls still work.
  startRunnerMusic();

  // Continuous lateral acceleration applied each substep while the
  // player holds a steering input. Tuned so a fully-held input feels
  // like decisive but not snappy steering — the ball arcs across the
  // tube in a beat or so, fast enough to feel responsive but slow
  // enough that the centerline spring still has authority once
  // released. We deliberately *don't* zero existing velocity; player
  // input bends the path, it doesn't reset it.
  const STEER_ACCEL = 90;
  function applySteering(subDt: number): void {
    if (dead || !steering) return;
    const len = Math.hypot(steering.dx, steering.dz) || 1;
    const ux = steering.dx / len;
    const uz = steering.dz / len;
    const accel = STEER_ACCEL * Math.max(0, Math.min(1, steering.mag));
    ball.vx += ux * accel * subDt;
    ball.vz += uz * accel * subDt;
    const speed = Math.hypot(ball.vx, ball.vz);
    if (speed > MAX_SPEED * 1.4) {
      const k = (MAX_SPEED * 1.4) / speed;
      ball.vx *= k;
      ball.vz *= k;
    }
  }

  // Wire the existing aim helper — drag-back becomes our nudge input.
  // We treat each shot as an instantaneous impulse rather than a
  // simulated trajectory, so installAim's onShoot fires applyNudge
  // immediately.
  let chargeAudio: ReturnType<typeof startCharge> | null = null;
  const ballPos = new Vector3();
  const aimDispose = installAim(scene.renderer.domElement, scene.camera, {
    getBallPosition: () => ballPos.set(ball.x, BALL_Y, ball.z),
    isInteractive: () => alive && !dead,
    onAimStart() {
      chargeAudio = startCharge();
    },
    onAimMove(input) {
      // Continuous swipe-to-steer. installAim returns aim vectors in
      // slingshot convention — a drag-right produces an aim pointing
      // LEFT (correct for the main golf game where you pull-back-to-
      // fire-forward). Runner is swipe-to-steer, so we invert so a
      // right-swipe nudges the ball right, matching the keyboard
      // mapping (ArrowRight → +dz).
      if (input.power <= 0.05) {
        steering = null;
        return;
      }
      steering = { dx: -input.aimX, dz: -input.aimZ, mag: input.power };
    },
    onAimCancel() {
      chargeAudio?.release();
      chargeAudio = null;
      steering = null;
    },
    onShoot() {
      chargeAudio?.release();
      chargeAudio = null;
      // Lift releases the held swipe — steering goes back to null
      // and the centerline spring eases the ball back to the wave.
      steering = null;
    },
  });

  // Desktop keyboard controls — left/right arrow keys (and A/D) feed
  // the same `steering` state the swipe handlers write to. Held keys
  // hold the steering vector live; releasing the last key clears it.
  const keysHeld = new Set<string>();
  function refreshKeyboardSteering(): void {
    if (keysHeld.size === 0) {
      // Keyboard release should only clear the state if the player
      // isn't *also* drag-steering with a pointer; if they are, the
      // pointer handler owns `steering` and we leave it alone. We
      // approximate "pointer is steering" by checking whether the
      // last write came from the pointer side — in practice both
      // inputs simultaneously is unusual, so a simple null-out is
      // good enough.
      steering = null;
      return;
    }
    let dx = 0;
    let dz = 0;
    if (keysHeld.has("ArrowLeft") || keysHeld.has("KeyA")) dz -= 1;
    if (keysHeld.has("ArrowRight") || keysHeld.has("KeyD")) dz += 1;
    if (keysHeld.has("ArrowUp") || keysHeld.has("KeyW")) dx += 1;
    if (keysHeld.has("ArrowDown") || keysHeld.has("KeyS")) dx -= 1;
    if (dx === 0 && dz === 0) {
      steering = null;
      return;
    }
    steering = { dx, dz, mag: 1 };
  }
  const onKeyDown = (e: KeyboardEvent): void => {
    if (
      e.code === "ArrowLeft" ||
      e.code === "ArrowRight" ||
      e.code === "ArrowUp" ||
      e.code === "ArrowDown" ||
      e.code === "KeyA" ||
      e.code === "KeyD" ||
      e.code === "KeyW" ||
      e.code === "KeyS"
    ) {
      e.preventDefault();
      keysHeld.add(e.code);
      refreshKeyboardSteering();
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keysHeld.delete(e.code);
    refreshKeyboardSteering();
  };
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);

  function emitState(): void {
    opts.onStateChange?.({
      distance: ball.x,
      speed: Math.hypot(ball.vx, ball.vz),
      lives,
      score,
      combo,
      bestScore,
      started,
    });
  }
  emitState();

  // The runner integrates its own physics step here rather than going
  // through simulateShot — simulateShot is one-shot (rest detection
  // ends it) and we need a continuous loop. The integration is the
  // same semi-implicit Euler / 240Hz substep pattern, just unrolled.
  function step(dtRaw: number): void {
    if (dead) return;
    const dt = Math.min(dtRaw, PHYSICS_DT * 8);
    const substeps = Math.max(1, Math.round(dt / PHYSICS_DT));
    const subDt = dt / substeps;

    // Combo decays after a stretch of no pickups. Doesn't tick before
    // the run has even started — gives the player a clean lobby
    // moment to drag-aim without combo-pressure ticking.
    const now = performance.now();
    if (started && combo > 1 && now - lastPickupAt > COMBO_TIMEOUT_MS) {
      combo = Math.max(1, combo - 1);
      lastPickupAt = now;
    }

    // Forward thrust + speed cap. Only kicks on AFTER the launch shot.
    // Scales gently with distance so the run ramps without snapping.
    const distanceM = Math.max(0, ball.x);
    const fwdAccel = started
      ? BASE_FWD_ACCEL + Math.min(MAX_FWD_ACCEL_BONUS, distanceM / 100)
      : 0;

    for (let s = 0; s < substeps; s++) {
      // Drain held steering input as a continuous lateral acceleration.
      // Runs first so it's visible to the centerline-spring guard
      // below — the spring only kicks in when the player isn't
      // actively steering, so input feels direct, not rubber-banded.
      applySteering(subDt);

      // Forward thrust applied along the local TANGENT to the
      // wave centerline — not pure +x — so the ball follows the
      // curve instead of carving across it. The previous version
      // pushed straight along +x while `vz` got jerked back to the
      // centerline by an undamped spring → zigzag.
      if (started) {
        const dz = trackDz(ball.x);
        const tlen = Math.hypot(1, dz);
        const tx = 1 / tlen;
        const tzDir = dz / tlen;
        ball.vx += fwdAccel * tx * subDt;
        ball.vz += fwdAccel * tzDir * subDt;

        // Critically-damped spring on the perpendicular offset from
        // the centerline. Skipped while the player is actively
        // steering so the input feels direct — once they release,
        // the spring takes over and eases the ball back to the wave.
        if (!steering) {
          const targetZ = trackZ(ball.x);
          const offset = targetZ - ball.z;
          const perpX = -tzDir; // perpendicular to tangent (rotated 90°)
          const perpZ = tx;
          // Project offset onto perp axis. (For a flat-ish wave, the
          // offset is mostly in z, so this resolves to ≈ offset.)
          const offsetPerp = perpX * 0 + perpZ * offset;
          // Project current velocity onto perp axis.
          const vPerp = perpX * ball.vx + perpZ * ball.vz;
          const restore =
            offsetPerp * CENTERLINE_STIFFNESS - vPerp * CENTERLINE_DAMPING;
          ball.vx += perpX * restore * subDt;
          ball.vz += perpZ * restore * subDt;
        }
      }

      // Gravity from every active planet (capped — see MAX_ACCEL).
      let ax = 0;
      let az = 0;
      for (const p of allPlanets) {
        // Cull planets > 60 units behind so we don't waste cycles on
        // dead bodies a runner has long since blown past.
        if (p.cx < ball.x - 60) continue;
        const dx = p.cx - ball.x;
        const dz = p.cz - ball.z;
        const r2 = dx * dx + dz * dz + GRAVITY_SOFTENING * GRAVITY_SOFTENING;
        const inv = p.strength / r2;
        const r = Math.sqrt(r2);
        ax += (dx / r) * inv;
        az += (dz / r) * inv;
      }
      const accelMag = Math.hypot(ax, az);
      if (accelMag > MAX_ACCEL) {
        ax = (ax / accelMag) * MAX_ACCEL;
        az = (az / accelMag) * MAX_ACCEL;
      }
      ball.vx += ax * subDt;
      ball.vz += az * subDt;

      // Air damping — gentler than simulateShot's because we want
      // forward speed to grow, not bleed. Apply only to lateral.
      ball.vz *= AIR_DAMPING;

      // Hard cap on overall speed so deep-distance runs stay
      // controllable instead of becoming a 60 u/s blur.
      const sp = Math.hypot(ball.vx, ball.vz);
      if (sp > MAX_SPEED) {
        const k = MAX_SPEED / sp;
        ball.vx *= k;
        ball.vz *= k;
      }

      ball.x += ball.vx * subDt;
      ball.z += ball.vz * subDt;

      // Wall bounce: ball is OUT iff it sits outside ALL capsules.
      // Same logic as simulateShot's capsule-arena check, but inlined
      // so we can run it every substep without a function call.
      let bestSlack = -Infinity;
      let outSeg: ArenaCapsule | null = null;
      let outCx = 0;
      let outCz = 0;
      for (const seg of segments) {
        const dseg = distPointSegment(
          ball.x,
          ball.z,
          seg.ax,
          seg.az,
          seg.bx,
          seg.bz,
        );
        const slack = seg.radius - BALL_RADIUS - dseg;
        if (slack > 0) {
          // Inside this capsule — no correction needed.
          bestSlack = Math.max(bestSlack, slack);
          outSeg = null;
          break;
        }
        if (slack > bestSlack) {
          bestSlack = slack;
          outSeg = seg;
          // Recompute the closest point only when we find the new
          // best penetration so we can reflect off it.
          const dx = seg.bx - seg.ax;
          const dz = seg.bz - seg.az;
          const len2 = dx * dx + dz * dz || 1;
          const t = Math.max(
            0,
            Math.min(1, ((ball.x - seg.ax) * dx + (ball.z - seg.az) * dz) / len2),
          );
          outCx = seg.ax + dx * t;
          outCz = seg.az + dz * t;
        }
      }
      if (outSeg) {
        // Push ball back to the wall + reflect lateral component.
        let nx = ball.x - outCx;
        let nz = ball.z - outCz;
        const nlen = Math.hypot(nx, nz) || 1;
        nx /= nlen;
        nz /= nlen;
        ball.x = outCx + nx * (outSeg.radius - BALL_RADIUS);
        ball.z = outCz + nz * (outSeg.radius - BALL_RADIUS);
        const vn = ball.vx * nx + ball.vz * nz;
        if (vn > 0) {
          ball.vx -= 2 * vn * nx * (1 - WALL_RESTITUTION);
          ball.vz -= 2 * vn * nz * (1 - WALL_RESTITUTION);
          ball.vx *= WALL_RESTITUTION;
          ball.vz *= WALL_RESTITUTION;
        }
      }

      // Pickup collection — orbs along the trackline. Sphere-vs-
      // sphere collision against ball + pickup radius. Collected
      // pickups dissolve via a quick scale-out then unmount.
      for (const pk of pickups) {
        if (pk.collected || !pk.mesh) continue;
        // Cheap horizontal-only check (we don't track y for pickups —
        // they sit at BALL_Y same as the ball).
        const ddx = ball.x - pk.x;
        const ddz = ball.z - pk.z;
        if (ddx * ddx + ddz * ddz < (BALL_RADIUS + PICKUP_RADIUS) ** 2) {
          pk.collected = true;
          combo = Math.min(COMBO_MAX, combo + 1);
          lastPickupAt = performance.now();
          score += PICKUP_BASE_VALUE * combo;
          const target = pk.mesh;
          // Animate scale-out + remove. Reuse the renderer's frame
          // loop via a setTimeout — cheap and good-enough for what
          // is essentially a confetti pop.
          target.scale.set(1.6, 1.6, 1.6);
          (target.material as MeshBasicMaterial).opacity = 1;
          window.setTimeout(() => {
            if (!alive) return;
            pickupRoot.remove(target);
            target.geometry = pickupGeom; // shared, don't dispose
            (target.material as MeshBasicMaterial).dispose();
          }, 200);
          pk.mesh = null;
          sfx.uiClick();
        }
      }

      // Planet collision — same restitution model as simulateShot
      // but with a hit-cooldown gate so we don't drain lives in a
      // single tunnel-collision frame.
      const inIFrames = now - lastHitAt < HIT_IFRAMES_MS;
      let nearestEdgeDist = Infinity;
      for (const p of allPlanets) {
        if (p.cx < ball.x - 60) continue;
        const dx = ball.x - p.cx;
        const dz = ball.z - p.cz;
        const d = Math.hypot(dx, dz);
        const minD = p.radius + BALL_RADIUS;
        const edge = d - minD;
        if (edge < nearestEdgeDist) nearestEdgeDist = edge;
        if (edge < 0) {
          // Penetrating — push back to surface and reflect velocity.
          const nx = dx / (d || 1);
          const nz = dz / (d || 1);
          ball.x = p.cx + nx * minD;
          ball.z = p.cz + nz * minD;
          const vn = ball.vx * nx + ball.vz * nz;
          if (vn < 0) {
            ball.vx -= (1 + PLANET_RESTITUTION) * vn * nx;
            ball.vz -= (1 + PLANET_RESTITUTION) * vn * nz;
          }
          // Even outside iframes we want a sparks pop on grazes —
          // gives the player tactile feedback on close calls. The
          // per-planet cooldown gates repeats from a single sustained
          // collision frame.
          const lastSparked = planetSparkCooldown.get(p) ?? -Infinity;
          if (now - lastSparked > SPARK_COOLDOWN_MS) {
            planetSparkCooldown.set(p, now);
            // Ball-position contact, biased outward along the
            // collision normal so the sparks fan away from the
            // planet, not into it.
            const sx = ball.x + nx * 0.3;
            const sz = ball.z + nz * 0.3;
            sparks.burst(sx, BALL_Y, sz, new Color("#ffb766"), 28);
            camera?.shake(0.55);
          }
          if (!inIFrames) {
            lives -= 1;
            // A hit resets the combo entirely — risk/reward.
            combo = 1;
            lastHitAt = now;
            sfx.collide(1);
            // Stronger shake on a real life-loss hit so the player
            // feels the cost.
            camera?.shake(0.95);
            if (lives <= 0) {
              dead = true;
              if (score > bestScore) {
                bestScore = score;
                saveBestScore(bestScore);
              }
              opts.onGameOver?.({
                distance: Math.round(ball.x),
                score,
                bestScore,
              });
              emitState();
              return;
            }
            emitState();
          }
        }
      }
      // Reserved for a future "near miss" bonus — currently unused
      // but cheap to keep computed since the loop already iterated
      // every planet.
      void nearestEdgeDist;
    }
  }

  let lastEmitT = 0;
  const off = scene.onFrame((t, dt) => {
    if (!alive || !visuals || !camera) return;

    if (!dead) {
      step(dt);
      ensureCorridor(ball.x);
    }

    // Update ball mesh + trail.
    visuals.ball.position.set(ball.x, BALL_Y, ball.z);
    trailHistory.push({ x: ball.x, y: BALL_Y, z: ball.z });
    while (trailHistory.length > TRAIL_LIMIT) trailHistory.shift();
    updateTrail(visuals, trailHistory, trailHistory.length);

    // Pickup orb wobble + bob — pure cosmetic polish so the orbs
    // feel alive in the camera. Uncollected pickups spin slowly
    // around their y axis with a small vertical bob.
    for (const pk of pickups) {
      if (!pk.mesh || pk.collected) continue;
      pk.mesh.rotation.y = t * 1.6 + pk.x * 0.03;
      pk.mesh.position.y = BALL_Y + Math.sin(t * 2.5 + pk.x * 0.2) * 0.18;
    }

    // We deliberately DON'T write `uBallPos` here. The orbital-chart
    // shader uses uBallPos to glow under the ball as a "you are here"
    // proximity grid, which reads as gameplay-relevant in the main
    // golf game but visually crowds the runner's wave-track. Leaving
    // uBallPos parked at the layout's tee (off-screen at x=-2000)
    // suppresses the grid-under-ball highlight while keeping the
    // chart's SDF-clipped corridor visible.
    if (visuals.boundaryMat.uniforms.uTime) {
      visuals.boundaryMat.uniforms.uTime.value = t;
    }
    visuals.boundaryParticles.update(t);
    sparks.update(t);
    for (const p of visuals.planets) {
      p.rotation.y += (p.userData.spin ?? 0.02) * 0.02;
      const u = (p.material as { uniforms?: { uTime?: { value: number } } })
        .uniforms;
      if (u?.uTime) u.uTime.value = t;
    }
    for (const w of visuals.gravityWells) {
      const u = (w.material as { uniforms?: { uTime?: { value: number } } })
        .uniforms;
      if (u?.uTime) u.uTime.value = t;
    }
    camera.followBall(ball.x, BALL_Y, ball.z, 0.08, t);
    // Pull camera closer than HoleCamera's third-person framing —
    // runner mode wants speed-feel, not the wide overview the daily
    // golf cam targets. Override after followBall so the lookahead
    // and shake logic inside HoleCamera still run, then snap-lerp
    // position + look-at to a tight chase. Tunings:
    //   back  16u → ball is large and forward motion reads
    //   height 5u → just above the wave so collisions feel close
    //   look ahead 18u → enough horizon for planet dodging
    runnerCamPos.set(ball.x - 16, BALL_Y + 5, ball.z);
    scene.camera.position.lerp(runnerCamPos, 0.22);
    runnerCamLook.set(ball.x + 18, 0, ball.z);
    scene.camera.lookAt(runnerCamLook);

    // Throttle HUD updates to ~10Hz so we don't slam React-style DOM
    // writes every frame for moving distance counters.
    if (t - lastEmitT > 0.1) {
      lastEmitT = t;
      emitState();
    }
  });

  return {
    dispose() {
      if (!alive) return;
      alive = false;
      off();
      aimDispose();
      chargeAudio?.release();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      stopRunnerMusic();
      disposeVisuals();
      scene.scene.remove(sparks.points);
      sparks.dispose();
      // Drop pickup meshes + the shared geometry. Pickup materials
      // were already disposed individually as they got collected;
      // any survivors get cleaned here.
      for (const pk of pickups) {
        if (pk.mesh) {
          pickupRoot.remove(pk.mesh);
          (pk.mesh.material as MeshBasicMaterial).dispose();
        }
      }
      scene.scene.remove(pickupRoot);
      pickupGeom.dispose();
    },
  };
}
